import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { useOverview } from '../lib/api';
import { SourceLink } from './_shared';

// Tab 1 — Overview. KPI tiles + the two market bar charts + sourced editorial highlights.
// All numbers are computed by the WWD Api from the rigs/wells tables (never stored), so
// they stay honest as contractor ingests land.
export default function Overview() {
  const { data: overview, isLoading, error } = useOverview();

  if (isLoading) return <LoadingSkeleton lines={8} />;
  if (error || !overview) return <ErrorState message={(error as Error | null)?.message} />;

  const { kpis, charts, highlights } = overview;

  const kpiTiles = [
    { label: 'Tracked rigs', value: kpis.trackedRigsTotal },
    { label: 'Drillships', value: kpis.trackedDrillships },
    { label: 'Semisubs', value: kpis.trackedSemisubs },
    { label: 'Jack-ups', value: kpis.trackedJackups },
    { label: 'Platforms', value: kpis.trackedPlatforms },
    { label: 'Confirmed active', value: kpis.activeRigsTotal },
    { label: 'High-impact wells 2026', value: kpis.highImpactWells2026 },
  ];

  return (
    <div className="stagger">
      <Card>
        <CardTitle>World Wide Drilling</CardTitle>
        <CardDescription className="mt-2">
          The offshore drilling market at a glance — rig fleet, key wells and regional activity.
          Every fact carries its source. Generated {overview.generatedAt}.
        </CardDescription>
      </Card>

      {/* KPI tile row */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        {kpiTiles.map((t) => (
          <div
            key={t.label}
            className="bg-surface border border-border rounded-(--radius-md) px-4 py-4 shadow-[var(--shadow-sm)]"
          >
            <div className="font-(family-name:--font-heading) text-3xl font-bold text-text [font-variant-numeric:tabular-nums]">
              {t.value}
            </div>
            <div className="mt-1 font-(family-name:--font-ui) text-[0.7rem] uppercase tracking-[0.14em] text-muted">
              {t.label}
            </div>
          </div>
        ))}
      </div>

      {/* Largest markets */}
      <Card>
        <span className="eyebrow">Largest markets</span>
        {kpis.largestMarkets.length === 0 ? (
          <p className="mt-3 text-sm text-muted">
            No rigs tracked yet — market rankings appear as contractor fleets are ingested.
          </p>
        ) : (
          <ol className="mt-3 flex flex-col gap-2">
            {kpis.largestMarkets.map((m, i) => (
              <li key={m.region.slug} className="flex items-baseline gap-3 text-sm">
                <span className="w-5 text-right font-(family-name:--font-ui) text-muted [font-variant-numeric:tabular-nums]">
                  {i + 1}.
                </span>
                <span className="font-medium text-text">{m.region.name}</span>
                <span className="text-muted">
                  {m.rigs} tracked {m.rigs === 1 ? 'rig' : 'rigs'}
                </span>
              </li>
            ))}
          </ol>
        )}
      </Card>

      {/* Bar charts */}
      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <BarChart
          title="High-impact wells by region"
          rows={charts.highImpactWellsByRegion.map((r) => ({ label: r.region.name, count: r.count }))}
          emptyNote="No high-impact wells tracked yet."
        />
        <BarChart
          title="Active floaters by region"
          rows={charts.activeFloatersByRegion.map((r) => ({ label: r.region.name, count: r.count }))}
          emptyNote="No active floaters tracked yet — this chart fills as contractor fleets are ingested."
        />
      </div>

      {/* Highlights — sourced editorial picks; rendered only when present */}
      {highlights.length > 0 && (
        <Card>
          <span className="eyebrow">Highlights</span>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {highlights.map((h, i) => (
              <div
                key={i}
                className="bg-surface-elevated border border-border rounded-(--radius-md) p-4"
              >
                <div className="mb-2 flex items-center gap-2">
                  <span
                    className={
                      'inline-block rounded-(--radius-sm) border bg-surface px-2 py-0.5 font-(family-name:--font-ui) text-[0.65rem] font-medium uppercase tracking-[0.12em] ' +
                      (h.kind === 'discovery' ? 'border-accent text-accent' : 'border-accent-info text-accent-info')
                    }
                  >
                    {h.kind}
                  </span>
                  <span className="text-xs text-muted">{h.date}</span>
                </div>
                <h3 className="text-base font-semibold text-text">{h.title}</h3>
                <p className="mt-1 text-sm text-muted">{h.body}</p>
                <div className="mt-2">
                  <SourceLink source={h.source} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/** Pure-div horizontal bar chart: label | proportional bar | count. */
function BarChart({
  title,
  rows,
  emptyNote,
}: {
  title: string;
  rows: { label: string; count: number }[];
  emptyNote: string;
}) {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  return (
    <Card className="mb-0">
      <span className="eyebrow">{title}</span>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-muted">{emptyNote}</p>
      ) : (
        <div className="mt-4 flex flex-col gap-2">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center gap-3 text-sm">
              <span className="w-36 flex-shrink-0 truncate text-muted" title={r.label}>
                {r.label}
              </span>
              <span className="h-3 flex-1 overflow-hidden rounded-[2px] bg-surface-elevated">
                <span
                  className="block h-full rounded-[2px] bg-accent"
                  style={{ width: `${max > 0 ? (r.count / max) * 100 : 0}%` }}
                />
              </span>
              <span className="w-8 flex-shrink-0 text-right font-(family-name:--font-ui) font-semibold text-text [font-variant-numeric:tabular-nums]">
                {r.count}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
